import React, { createContext, useState, useEffect } from 'react';
import authService from '../api/authService';

/**
 * @description Contexto de React para la autenticación.
 * Proporciona acceso al estado del usuario actual y a las funciones de autenticación.
 * @type {React.Context<object>}
 */
export const AuthContext = createContext();

/**
 * @description Proveedor del contexto de autenticación.
 * Gestiona el estado del usuario (`currentUser`), y proporciona las funciones `login` y `logout`.
 * @param {object} props Las propiedades del componente.
 * @param {React.ReactNode} props.children Los componentes hijos que tendrán acceso al contexto.
 * @returns {JSX.Element}
 */
export const AuthProvider = ({ children }) => {
  // `undefined` indica que el estado de autenticación aún no se ha determinado.
  // `null` indica que no hay un usuario autenticado.
  // Un objeto de usuario indica que el usuario está autenticado.
  const [currentUser, setCurrentUser] = useState(undefined);

  useEffect(() => {
    // Al iniciar la aplicación, se comprueba si hay datos de usuario en el localStorage.
    const user = authService.getCurrentUser();
    if (user) {
      setCurrentUser(user);
    } else {
      setCurrentUser(null); // Marcar explícitamente que no hay usuario.
    }
  }, []);

  /**
   * @description Maneja el proceso de inicio de sesión.
   * @param {string} email El email del usuario.
   * @param {string} password La contraseña del usuario.
   */
  const login = async (email, password) => {
    await authService.login(email, password);
    // Usar getCurrentUser para obtener el objeto enriquecido con los claims del JWT (role, email, user_id)
    setCurrentUser(authService.getCurrentUser());
  };

  /**
   * @description Maneja el proceso de cierre de sesión.
   */
  const logout = () => {
    authService.logout();
    setCurrentUser(null);
  };

  // Valor que se pasará a los consumidores del contexto.
  const value = {
    currentUser,
    login,
    logout,
  };

  // El Provider hace que el objeto `value` esté disponible para cualquier componente hijo que lo consuma.
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};